import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_SUMMARY_LAYOUT,
  SUMMARY_BUILTIN_TITLES,
  SUMMARY_FIELD_KEYS,
  SUMMARY_FIELD_LABELS,
  SUMMARY_MAX_STATS,
  effectiveSummaryLayout,
  layoutForSave,
  normalizeSummaryLayout,
  preferredContactGap,
  summaryCardTitle,
  type SummaryCard,
  type SummaryFieldKey,
  type SummaryItem,
  type SummaryLayout,
  type SummaryLayouts,
} from '@ehr/shared';
import { PreferredContactWarning } from './PreferredContactWarning.js';
import { api, ApiError } from '../lib/api.js';
import {
  builtinCards,
  clientFieldValues,
  fieldAnswers,
  fmtWhen,
  nothingOnFile,
  type Demographics,
  type SummaryFormFeed,
} from '../lib/summaryData.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import type { LibForm } from './FormModal.js';

/**
 * ehr#230 - the client chart's Summary tab (Chase's SUMMARY_TAB_B10 + handoff V7), phase 1:
 * the header strip, the seven Demographics cards, From forms and custom cards fed by the
 * client's form answers, card notes saved to the client record, and edit mode (hide / show,
 * pointer drag for cards and header fields, + Add fields, + New card, My layout / Practice
 * default, Reset, Cancel, Save layout). Form write-back into Demographics, the override
 * dialog and the client-portal review banner wait for the spec (SUMMARY_TAB_SPEC_10-03-V3).
 */

const I = {
  grip: (
    <svg viewBox="0 0 16 16" aria-hidden>
      {[3, 8, 13].flatMap((y) =>
        [5, 11].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.4" />),
      )}
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M17.9 17.9A10 10 0 0 1 12 20C5 20 1 12 1 12a18 18 0 0 1 5.1-5.9M9.9 4.2A9 9 0 0 1 12 4c7 0 11 8 11 8a18 18 0 0 1-2.2 3.2M1 1l22 22" />
    </svg>
  ),
  trash: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
    </svg>
  ),
  x: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  ),
  cog: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  search: (
    <svg viewBox="0 0 24 24" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.3-4.3" />
    </svg>
  ),
};

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T;
const newCardId = () => `c-${Math.random().toString(36).slice(2, 10)}`;
const sameItem = (a: SummaryItem, b: SummaryItem) =>
  a.t === b.t &&
  (a.t !== 'form' || (b.t === 'form' && a.f === b.f && a.q === b.q)) &&
  (a.t !== 'demo' || (b.t === 'demo' && a.k === b.k));

type PickerForm = { key: string; name: string; fields: { id: string; label: string }[] };
type Drag = {
  kind: 'card' | 'stat';
  id: string;
  el: HTMLElement;
  x0: number;
  y0: number;
  started: boolean;
  ghost: HTMLElement | null;
  dx: number;
  dy: number;
  target: string | null;
  after: boolean;
  lastY: number;
  lastX: number;
};

export function SummaryTab({
  contactId,
  refreshKey = 0,
  firstName,
  data,
  balanceCents,
  onOpenSection,
  onOpenForms,
}: {
  contactId: string;
  /** ehr#342: bumps when the chart's stamp moves (a teammate's change). */
  refreshKey?: number;
  firstName: string;
  data: Demographics;
  balanceCents: number | null;
  /** Open the Profile tab at a section (a built-in card's Edit / Add). */
  onOpenSection: (section: string) => void;
  /** Open the Forms tab (the empty state's Send intake form). */
  onOpenForms: () => void;
}) {
  const [layouts, setLayouts] = useState<SummaryLayouts | null>(null);
  const [feed, setFeed] = useState<SummaryFormFeed[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [library, setLibrary] = useState<LibForm[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [editing, setEditing] = useState(false);
  const [work, setWork] = useState<SummaryLayout>(DEFAULT_SUMMARY_LAYOUT);
  const [scope, setScope] = useState<'me' | 'practice'>('me');
  const [saving, setSaving] = useState(false);
  const [openHist, setOpenHist] = useState<Set<string>>(new Set());
  const [picker, setPicker] = useState<{
    cardId: string;
    tab: 'forms' | 'client' | 'note';
    form: string | null;
    q: string;
    sel: Record<string, boolean>;
  } | null>(null);
  const [confirm, setConfirm] = useState<{
    message: ReactNode;
    label: string;
    onOk: () => void;
  } | null>(null);
  const [toast, setToast] = useState('');
  const toastTimer = useRef<number | undefined>(undefined);
  const say = (m: string) => {
    setToast(m);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(''), 2600);
  };

  // ── load ──
  useEffect(() => {
    let alive = true;
    setStatus('loading');
    Promise.all([
      api.get<SummaryLayouts>('/summary-layout'),
      api.get<{ notes: Record<string, string>; forms: SummaryFormFeed[] }>(
        `/contacts/${contactId}/summary`,
      ),
    ])
      .then(([l, s]) => {
        if (!alive) return;
        setLayouts(l);
        setFeed(s.forms);
        setNotes(s.notes);
        setStatus('ready');
      })
      .catch((e: unknown) => {
        if (alive) setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error');
      });
    // Custom Form Builder forms: labels for their fields, and the + Add fields list.
    api
      .get<{ custom: LibForm[] }>('/forms-library')
      .then((r) => alive && setLibrary(r.custom ?? []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [contactId]);

  const view = useMemo(
    () => effectiveSummaryLayout(layouts?.practice ?? null, layouts?.mine ?? null),
    [layouts],
  );
  const L = editing ? work : view;

  // ── values ──
  const values = useMemo(() => clientFieldValues(data, { balanceCents }), [data, balanceCents]);
  const builtins = useMemo(() => builtinCards(data), [data]);
  // ehr#661: the preferred contact method has nothing to reach.
  const prefGap = useMemo(() => preferredContactGap(data.contact), [data]);
  const feedByKey = useMemo(() => new Map(feed.map((f) => [f.key, f])), [feed]);
  const libByKey = useMemo(() => new Map(library.map((f) => [`f:${f.id}`, f])), [library]);
  const formName = (key: string) => feedByKey.get(key)?.name ?? libByKey.get(key)?.name ?? 'Form';
  const fieldLabel = (key: string, q: string) =>
    feedByKey.get(key)?.fields.find((f) => f.id === q)?.label ??
    libByKey.get(key)?.fields?.find((f) => f.id === q)?.label ??
    (q.startsWith('label:') ? q.slice(6) : 'Question');
  const pickerForms = useMemo<PickerForm[]>(() => {
    const out = new Map<string, PickerForm>();
    for (const f of feed) {
      out.set(f.key, {
        key: f.key,
        name: f.name,
        fields: f.fields.map((x) => ({
          id: x.id,
          label:
            x.label ?? libByKey.get(f.key)?.fields?.find((l) => l.id === x.id)?.label ?? 'Question',
        })),
      });
    }
    for (const lf of library) {
      const key = `f:${lf.id}`;
      const cur = out.get(key);
      const libFields = (lf.fields ?? [])
        .filter((x) => x.type !== 'heading' && x.label)
        .map((x) => ({ id: x.id, label: x.label }));
      if (!cur) out.set(key, { key, name: lf.name, fields: libFields });
      else
        for (const x of libFields) if (!cur.fields.some((y) => y.id === x.id)) cur.fields.push(x);
    }
    return [...out.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [feed, library, libByKey]);

  // ── layout edits (edit mode only) ──
  const edit = (fn: (l: SummaryLayout) => void) =>
    setWork((w) => {
      const n = clone(w);
      fn(n);
      return n;
    });
  const cardBy = (l: SummaryLayout, id: string) => l.cards.find((c) => c.id === id);

  const startEdit = () => {
    setWork(clone(view));
    setScope('me'); // handoff 7a: edit mode always opens on My layout
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    say('No changes saved.');
  };
  const saveEdit = () => {
    setSaving(true);
    const layout = layoutForSave(work);
    api
      .put<SummaryLayouts>('/summary-layout', { scope, layout })
      .then((l) => {
        setLayouts(l);
        setEditing(false);
        say(
          scope === 'me'
            ? 'Layout saved for you. The practice default is unchanged.'
            : 'Practice default saved for everyone.',
        );
      })
      .catch(() => say('The layout could not be saved. Try again.'))
      .finally(() => setSaving(false));
  };
  const resetLayout = () =>
    setConfirm({
      message: (
        <>
          <strong>Reset to the practice default?</strong>
          <p style={{ marginTop: 6 }}>
            Your card order, hidden cards, header fields and custom cards go back to the layout your
            admin set. Notes typed on the client record are kept.
          </p>
        </>
      ),
      label: 'Reset layout',
      onOk: () => {
        setWork(normalizeSummaryLayout(layouts?.practice ?? DEFAULT_SUMMARY_LAYOUT));
        say('Layout reset. Click Save layout to keep it.');
      },
    });

  // ── notes: save to the client record as typed (handoff 7c) ──
  const noteTimers = useRef<Record<string, number>>({});
  const typedNotes = useRef(new Set<string>());
  const onNote = (cardId: string, text: string) => {
    typedNotes.current.add(cardId);
    setNotes((n) => ({ ...n, [cardId]: text }));
    window.clearTimeout(noteTimers.current[cardId]);
    noteTimers.current[cardId] = window.setTimeout(() => {
      api
        .put(`/contacts/${contactId}/summary/notes/${encodeURIComponent(cardId)}`, { text })
        .catch(() => say('The note could not be saved. Check your connection.'));
    }, 600);
  };
  useEffect(() => {
    const timers = noteTimers.current;
    return () => Object.values(timers).forEach((t) => window.clearTimeout(t));
  }, []);

  // ehr#342 - a teammate's change: reload the cards' filled forms and notes without "Loading…".
  // A note typed here keeps what was typed (its own save also moves the stamp), and nothing
  // reloads while the layout is being edited.
  const editingRef = useRef(editing);
  editingRef.current = editing;
  useEffect(() => {
    if (!refreshKey || editingRef.current) return;
    let alive = true;
    api
      .get<{ notes: Record<string, string>; forms: SummaryFormFeed[] }>(
        `/contacts/${contactId}/summary`,
      )
      .then((s) => {
        if (!alive) return;
        setFeed(s.forms);
        setNotes((cur) => {
          const next = { ...s.notes };
          for (const id of typedNotes.current) next[id] = cur[id] ?? '';
          return next;
        });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [refreshKey, contactId]);

  // ── pointer drag: cards by their title bar, header fields anywhere but their controls ──
  const rootRef = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  const scrollParent = useCallback((): HTMLElement | null => {
    let el = rootRef.current?.parentElement ?? null;
    while (el) {
      const oy = getComputedStyle(el).overflowY;
      if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return null;
  }, []);
  const clearDrop = () =>
    rootRef.current
      ?.querySelectorAll('.sm-drop-before,.sm-drop-after,.sm-drop-above,.sm-drop-below')
      .forEach((n) =>
        n.classList.remove('sm-drop-before', 'sm-drop-after', 'sm-drop-above', 'sm-drop-below'),
      );
  const onPointerDown = (e: React.PointerEvent, kind: 'card' | 'stat', id: string) => {
    if (!editing || e.button > 0) return;
    if ((e.target as HTMLElement).closest('button,input,select,textarea,a')) return;
    e.preventDefault();
    drag.current = {
      kind,
      id,
      el: e.currentTarget.closest(kind === 'card' ? '.sm-card' : '.sm-stat') as HTMLElement,
      x0: e.clientX,
      y0: e.clientY,
      started: false,
      ghost: null,
      dx: 0,
      dy: 0,
      target: null,
      after: false,
      lastX: e.clientX,
      lastY: e.clientY,
    };
  };
  useEffect(() => {
    if (!editing) return;
    let raf = 0;
    const autoScroll = () => {
      const d = drag.current;
      if (!d?.started) {
        raf = 0;
        return;
      }
      const sp = scrollParent();
      const top = sp ? sp.getBoundingClientRect().top : 0;
      const bottom = sp ? sp.getBoundingClientRect().bottom : window.innerHeight;
      const edge = 80;
      let v = 0;
      if (d.lastY < top + edge) v = -Math.ceil((top + edge - d.lastY) / 5);
      else if (d.lastY > bottom - edge) v = Math.ceil((d.lastY - (bottom - edge)) / 5);
      if (v) (sp ?? document.scrollingElement)?.scrollBy(0, v);
      raf = requestAnimationFrame(autoScroll);
    };
    const move = (e: PointerEvent) => {
      const d = drag.current;
      if (!d) return;
      if (!d.started) {
        if (Math.abs(e.clientX - d.x0) + Math.abs(e.clientY - d.y0) < 6) return;
        d.started = true;
        const r = d.el.getBoundingClientRect();
        d.dx = e.clientX - r.left;
        d.dy = e.clientY - r.top;
        // The floating copy carries no data-cid / data-sid, so lookups never hit it (7e).
        const g = d.el.cloneNode(true) as HTMLElement;
        g.removeAttribute('data-cid');
        g.removeAttribute('data-sid');
        g.setAttribute('aria-hidden', 'true');
        g.classList.add('sm-ghost');
        g.style.width = `${r.width}px`;
        document.body.appendChild(g);
        d.ghost = g;
        d.el.classList.add('sm-dragging');
        document.body.classList.add('sm-is-dragging');
      }
      e.preventDefault();
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      if (d.ghost) {
        d.ghost.style.left = `${e.clientX - d.dx}px`;
        d.ghost.style.top = `${e.clientY - d.dy}px`;
      }
      clearDrop();
      d.target = null;
      const under = document.elementFromPoint(e.clientX, e.clientY);
      if (d.kind === 'stat') {
        const st = under?.closest('.sm-stat[data-sid]') as HTMLElement | null;
        if (st && st.dataset.sid !== d.id && rootRef.current?.contains(st)) {
          const r = st.getBoundingClientRect();
          d.after = e.clientX - r.left > r.width / 2;
          st.classList.add(d.after ? 'sm-drop-after' : 'sm-drop-before');
          d.target = st.dataset.sid ?? null;
        }
      } else {
        const c = under?.closest('.sm-card[data-cid]') as HTMLElement | null;
        if (c && c.dataset.cid !== d.id && rootRef.current?.contains(c)) {
          const r = c.getBoundingClientRect();
          const wide = c.classList.contains('sm-span2') || window.innerWidth <= 760;
          d.after = wide ? e.clientY - r.top > r.height / 2 : e.clientX - r.left > r.width / 2;
          c.classList.add(
            d.after
              ? wide
                ? 'sm-drop-below'
                : 'sm-drop-after'
              : wide
                ? 'sm-drop-above'
                : 'sm-drop-before',
          );
          d.target = c.dataset.cid ?? null;
        }
      }
      if (!raf) raf = requestAnimationFrame(autoScroll);
    };
    const end = () => {
      const d = drag.current;
      drag.current = null;
      document.body.classList.remove('sm-is-dragging');
      if (!d?.started) return;
      d.ghost?.remove();
      d.el.classList.remove('sm-dragging');
      clearDrop();
      if (!d.target) return;
      const target = d.target;
      if (d.kind === 'stat') {
        edit((l) => {
          const k = d.id as SummaryFieldKey;
          l.stats.splice(l.stats.indexOf(k), 1);
          const ti = l.stats.indexOf(target as SummaryFieldKey);
          l.stats.splice(d.after ? ti + 1 : ti, 0, k);
        });
        say('Field moved. Click Save layout to keep it.');
      } else {
        edit((l) => {
          const from = l.cards.findIndex((c) => c.id === d.id);
          const [moving] = l.cards.splice(from, 1);
          const ti = l.cards.findIndex((c) => c.id === target);
          if (moving) l.cards.splice(d.after ? ti + 1 : ti, 0, moving);
        });
        say('Card moved. Click Save layout to keep it.');
      }
    };
    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [editing, scrollParent]);

  // ── field picker ──
  const openPicker = (cardId: string) =>
    setPicker({ cardId, tab: 'forms', form: null, q: '', sel: {} });
  const addPicked = () => {
    if (!picker) return;
    const picked = Object.keys(picker.sel).filter((k) => picker.sel[k]);
    edit((l) => {
      const c = cardBy(l, picker.cardId);
      if (!c || c.kind === 'builtin') return;
      for (const k of picked) {
        const [t, a, b] = k.split('|');
        const it: SummaryItem =
          t === 'form'
            ? { t: 'form', f: a!, q: b! }
            : t === 'demo'
              ? { t: 'demo', k: a as SummaryFieldKey }
              : { t: 'note' };
        if (!c.items.some((x) => sameItem(x, it))) c.items.push(it);
      }
    });
    setPicker(null);
    say(
      `${picked.length} ${picked.length === 1 ? 'field' : 'fields'} added. Click Save layout to keep it.`,
    );
  };

  // ── render pieces ──
  const histToggle = (id: string, n: number) => (
    <button
      type="button"
      className="sm-hist-btn"
      onClick={() =>
        setOpenHist((s) => {
          const x = new Set(s);
          if (x.has(id)) x.delete(id);
          else x.add(id);
          return x;
        })
      }
    >
      {openHist.has(id) ? 'Hide history' : `${n} earlier`}
    </button>
  );

  const itemView = (card: SummaryCard, it: SummaryItem, idx: number) => {
    const remove = editing ? (
      <button
        type="button"
        className="sm-item-x"
        aria-label="Remove field"
        onClick={() =>
          edit((l) => {
            const c = cardBy(l, card.id);
            if (c && c.kind !== 'builtin') c.items.splice(idx, 1);
          })
        }
      >
        {I.x}
      </button>
    ) : null;
    if (it.t === 'note') {
      return (
        <div className="sm-irow sm-note-box sm-full" key={`note-${idx}`}>
          <div className="sm-grow">
            <div className="sm-l">Note</div>
            <textarea
              value={notes[card.id] ?? ''}
              placeholder={`Type a note about ${firstName || 'this client'}. It saves to their record.`}
              onChange={(e) => onNote(card.id, e.target.value)}
              rows={3}
            />
            <div className="sm-nh">
              Saved to the client record. Only people with this card on their Summary see it.
            </div>
          </div>
          {remove}
        </div>
      );
    }
    if (it.t === 'demo') {
      const v = values[it.k];
      return (
        <div className="sm-irow" key={`demo-${it.k}`}>
          <div className="sm-grow sm-f">
            <div className="sm-l">{SUMMARY_FIELD_LABELS[it.k]}</div>
            <div className={`sm-v${v ? '' : ' sm-missing'}`}>{v || 'Not on file'}</div>
          </div>
          {remove}
        </div>
      );
    }
    const a = fieldAnswers(feedByKey.get(it.f), it.q);
    const hid = `${card.id}-${idx}`;
    return (
      <div className="sm-irow" key={`form-${it.f}-${it.q}-${idx}`}>
        <div className="sm-grow sm-f">
          <div className="sm-l">{fieldLabel(it.f, it.q)}</div>
          {a.latest ? (
            <>
              <div className="sm-v">{a.latest.value}</div>
              <div className="sm-src">
                {formName(it.f)}, {fmtWhen(a.latest.at)}
                {a.latest.source === 'portal' ? ' (client portal)' : ''}
                {a.earlier.length > 0 && histToggle(hid, a.earlier.length)}
              </div>
              {openHist.has(hid) && (
                <div className="sm-hist">
                  {a.earlier.map((h) => (
                    <div key={h.at}>
                      <s>{h.value}</s> {formName(it.f)}, {fmtWhen(h.at)}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="sm-v sm-missing">Not filled yet</div>
              <div className="sm-src">{formName(it.f)}</div>
            </>
          )}
        </div>
        {remove}
      </div>
    );
  };

  const cardView = (c: SummaryCard, span: boolean) => {
    const b = c.kind === 'builtin' ? builtins[c.id] : null;
    return (
      <section
        key={c.id}
        className={`sm-card${span ? ' sm-span2' : ''}`}
        data-cid={c.id}
        aria-label={summaryCardTitle(c)}
      >
        <div className="sm-c-head" onPointerDown={(e) => onPointerDown(e, 'card', c.id)}>
          {editing && (
            <span className="sm-handle" aria-label="Drag to move">
              {I.grip}
            </span>
          )}
          {editing && c.kind === 'custom' ? (
            <input
              className="sm-title-in"
              value={c.title}
              aria-label="Card name"
              onChange={(e) =>
                edit((l) => {
                  const x = cardBy(l, c.id);
                  if (x && x.kind === 'custom') x.title = e.target.value;
                })
              }
            />
          ) : (
            <h2>{summaryCardTitle(c)}</h2>
          )}
          {!editing && b && (
            <button type="button" className="sm-link" onClick={() => onOpenSection(b.section)}>
              {b.link}
            </button>
          )}
          {!editing && c.kind === 'forms' && (
            <span className="sm-count">
              {c.items.length} {c.items.length === 1 ? 'answer' : 'answers'}
            </span>
          )}
          {editing && (
            <div className="sm-ctrls">
              {c.kind !== 'builtin' && (
                <button type="button" className="sm-addf" onClick={() => openPicker(c.id)}>
                  +<span className="sm-t"> Add fields</span>
                </button>
              )}
              <button
                type="button"
                className="sm-cb"
                title="Hide card"
                aria-label="Hide card"
                onClick={() => edit((l) => void l.hidden.push(c.id))}
              >
                {I.eye}
              </button>
              {c.kind === 'custom' && (
                <button
                  type="button"
                  className="sm-cb sm-del"
                  aria-label="Delete card"
                  onClick={() =>
                    setConfirm({
                      message: (
                        <>
                          <strong>Delete &ldquo;{summaryCardTitle(c)}&rdquo;?</strong>
                          <p style={{ marginTop: 6 }}>
                            This removes the card from your Summary. Notes typed in it stay on the
                            client record.
                          </p>
                        </>
                      ),
                      label: 'Delete card',
                      onOk: () => {
                        edit((l) => {
                          l.cards = l.cards.filter((x) => x.id !== c.id);
                          l.hidden = l.hidden.filter((x) => x !== c.id);
                        });
                        say('Card deleted. Click Save layout to keep it.');
                      },
                    })
                  }
                >
                  {I.trash}
                </button>
              )}
            </div>
          )}
        </div>
        {b ? (
          b.nothing ? (
            <p className="sm-nothing">{b.nothing}</p>
          ) : (
            <>
              <div className="sm-fields">
                {b.fields.map((f) => (
                  <div key={f.label} className={`sm-f${f.full ? ' sm-full' : ''}`}>
                    <div className="sm-l">{f.label}</div>
                    <div className="sm-v">{f.value}</div>
                  </div>
                ))}
              </div>
              {b.empty.length > 0 && (
                <div className="sm-empty-line">
                  {b.empty.length} {b.empty.length === 1 ? 'field' : 'fields'} empty:{' '}
                  {b.empty.join(', ')}
                </div>
              )}
            </>
          )
        ) : c.kind === 'builtin' ? null : c.items.length === 0 ? (
          <p className="sm-blank">No fields yet.{editing ? ' Click + Add fields.' : ''}</p>
        ) : (
          <div className="sm-fields">{c.items.map((it, i) => itemView(c, it, i))}</div>
        )}
      </section>
    );
  };

  if (status === 'loading') return <p className="muted">Loading summary…</p>;
  if (status === 'forbidden')
    return <p className="muted">Your role cannot view this client&apos;s summary.</p>;
  if (status === 'error') return <p className="muted">The summary could not be loaded.</p>;

  if (!editing && nothingOnFile(data, feed)) {
    return (
      <div className="sm-root">
        <div className="sm-emptystate">
          <h2>Nothing on file yet</h2>
          <p>Fill in the Profile or send an intake form. Answers show up here on their own.</p>
          <div className="sm-row">
            <button type="button" className="btn" onClick={onOpenForms}>
              Send intake form
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => onOpenSection('contact')}
            >
              Open Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const visible = L.cards.filter((c) => !L.hidden.includes(c.id));
  const hiddenCards = L.cards.filter((c) => L.hidden.includes(c.id));
  const nStat = L.stats.length + (editing && L.stats.length < SUMMARY_MAX_STATS ? 1 : 0);
  const cog = !editing && (
    <button
      type="button"
      className="sm-cog"
      title="Customize layout"
      aria-label="Customize layout"
      onClick={startEdit}
    >
      {I.cog}
    </button>
  );

  return (
    <div className={`sm-root${editing ? ' sm-editing' : ''}`} ref={rootRef}>
      {!editing && prefGap && (
        <PreferredContactWarning gap={prefGap} onFix={() => onOpenSection('contact')} />
      )}
      {editing && (
        <div className="sm-edit-note">
          <span className="sm-edit-text">
            {scope === 'me'
              ? "Editing your layout. Drag a header field or a card (by its title bar) to move it, hide what you don't need, or add fields and new cards. Nothing changes until you click Save layout."
              : 'Editing the practice default (admin). Staff who have not personalized their Summary see this layout. Staff who have keep theirs and can pick it up with Reset to default.'}
          </span>
          <span className="sm-en-btns">
            <button type="button" className="btn sm" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary sm"
              onClick={saveEdit}
              disabled={saving}
            >
              Save layout
            </button>
          </span>
        </div>
      )}

      {(editing || L.stats.length > 0 || cog) && (
        <div className="sm-statrow">
          <div className="sm-stats" style={{ ['--n' as string]: Math.max(nStat, 1) }}>
            {L.stats.map((k, i) => (
              <div
                key={k}
                className="sm-stat"
                data-sid={k}
                onPointerDown={(e) => onPointerDown(e, 'stat', k)}
              >
                {editing && <span className="sm-sgrip">{I.grip}</span>}
                <div className="sm-l">{SUMMARY_FIELD_LABELS[k]}</div>
                <div className={`sm-v${values[k] ? '' : ' sm-missing'}`}>{values[k] || '—'}</div>
                {editing && (
                  <div className="sm-sctl">
                    <select
                      aria-label="Field"
                      value={k}
                      onChange={(e) =>
                        edit((l) => {
                          const nk = e.target.value as SummaryFieldKey;
                          if (!l.stats.includes(nk)) l.stats[i] = nk;
                        })
                      }
                    >
                      {SUMMARY_FIELD_KEYS.map((o) => {
                        const used = o !== k && L.stats.includes(o);
                        return (
                          <option key={o} value={o} disabled={used}>
                            {SUMMARY_FIELD_LABELS[o]}
                            {used ? ' (in use)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      className="sm-cb sm-del"
                      aria-label="Remove"
                      onClick={() => edit((l) => void l.stats.splice(i, 1))}
                    >
                      {I.x}
                    </button>
                  </div>
                )}
              </div>
            ))}
            {editing && L.stats.length < SUMMARY_MAX_STATS && (
              <button
                type="button"
                className="sm-stat sm-stat-add"
                onClick={() =>
                  edit((l) => {
                    const free = SUMMARY_FIELD_KEYS.find((k) => !l.stats.includes(k));
                    if (free) l.stats.push(free);
                  })
                }
              >
                + Add field
              </button>
            )}
          </div>
          {cog}
        </div>
      )}

      <div className="sm-grid">
        {visible.length || editing ? (
          visible.map((c, i) => cardView(c, visible.length % 2 === 1 && i === visible.length - 1))
        ) : (
          <p className="sm-blank sm-all-hidden">
            All cards are hidden. Click the settings cog to show them.
          </p>
        )}
      </div>

      {editing && (
        <>
          <button
            type="button"
            className="sm-newcard"
            onClick={() => {
              const id = newCardId();
              edit((l) => void l.cards.push({ id, kind: 'custom', title: 'New card', items: [] }));
              window.setTimeout(() => {
                const inp = rootRef.current?.querySelector<HTMLInputElement>(
                  `.sm-card[data-cid="${id}"] .sm-title-in`,
                );
                inp?.focus();
                inp?.select();
                inp?.scrollIntoView({ block: 'center', behavior: 'smooth' });
              }, 30);
            }}
          >
            + New card
          </button>
          <div className="sm-tray">
            <h4>Hidden cards</h4>
            {hiddenCards.length ? (
              <div className="sm-chips">
                {hiddenCards.map((c) => (
                  <span key={c.id} className="sm-tchip">
                    {summaryCardTitle(c)}
                    {c.kind === 'custom' && c.fromAdmin && (
                      <span className="sm-new-tag">New from practice default</span>
                    )}
                    <button
                      type="button"
                      onClick={() =>
                        edit((l) => {
                          const x = cardBy(l, c.id);
                          if (x && x.kind === 'custom') delete x.fromAdmin;
                          l.hidden = l.hidden.filter((h) => h !== c.id);
                        })
                      }
                    >
                      Show
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="sm-blank">None. Hide a card with its eye button and it waits here.</p>
            )}
          </div>
          <div className="sm-editbar">
            <span className="sm-lbl">Editing</span>
            {layouts?.canEditPractice && (
              <div className="sm-seg" role="group" aria-label="Which layout">
                <button
                  type="button"
                  className={scope === 'me' ? 'on' : ''}
                  onClick={() => setScope('me')}
                >
                  My layout
                </button>
                <button
                  type="button"
                  className={scope === 'practice' ? 'on' : ''}
                  onClick={() => setScope('practice')}
                >
                  Practice default (admin)
                </button>
              </div>
            )}
            <div className="sm-spacer" />
            <button type="button" className="btn" onClick={resetLayout} disabled={saving}>
              Reset to default
            </button>
            <button type="button" className="btn" onClick={cancelEdit} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={saveEdit} disabled={saving}>
              {saving ? 'Saving…' : 'Save layout'}
            </button>
          </div>
        </>
      )}

      {picker && (
        <SummaryPicker
          picker={picker}
          setPicker={setPicker}
          card={cardBy(work, picker.cardId)}
          forms={pickerForms}
          feedByKey={feedByKey}
          values={values}
          firstName={firstName}
          onAdd={addPicked}
        />
      )}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          confirmLabel={confirm.label}
          onConfirm={() => {
            const ok = confirm.onOk;
            setConfirm(null);
            ok();
          }}
          onClose={() => setConfirm(null)}
        />
      )}
      {toast && (
        <div className="sm-toast" role="status">
          {toast}
        </div>
      )}
    </div>
  );
}

type PickerState = {
  cardId: string;
  tab: 'forms' | 'client' | 'note';
  form: string | null;
  q: string;
  sel: Record<string, boolean>;
};

/** + Add fields (mockup FIELD PICKER): a form's fields, client info fields, a note box. */
function SummaryPicker({
  picker,
  setPicker,
  card,
  forms,
  feedByKey,
  values,
  firstName,
  onAdd,
}: {
  picker: PickerState;
  setPicker: (p: PickerState | null) => void;
  card: SummaryCard | undefined;
  forms: PickerForm[];
  feedByKey: Map<string, SummaryFormFeed>;
  values: Record<SummaryFieldKey, string>;
  firstName: string;
  onAdd: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPicker(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setPicker]);
  if (!card || card.kind === 'builtin') return null;
  const items = card.items;
  const has = (it: SummaryItem) => items.some((x) => sameItem(x, it));
  const set = (p: Partial<PickerState>) => setPicker({ ...picker, ...p });
  const toggle = (k: string, on: boolean) => set({ sel: { ...picker.sel, [k]: on } });
  const n = Object.values(picker.sel).filter(Boolean).length;
  const who = firstName || 'Client';
  const tabs: [PickerState['tab'], string][] = [['forms', 'Forms']];
  if (card.kind === 'custom') tabs.push(['client', 'Client info'], ['note', 'Note box']);
  const form = picker.form ? forms.find((f) => f.key === picker.form) : null;

  let body: ReactNode;
  if (picker.tab === 'forms' && !form) {
    const q = picker.q.toLowerCase();
    const list = forms.filter((f) => f.name.toLowerCase().includes(q));
    body = (
      <>
        <div className="sm-psearch">
          {I.search}
          <input
            autoFocus
            placeholder="Search forms, e.g. PHQ-9 or intake"
            value={picker.q}
            onChange={(e) => set({ q: e.target.value })}
          />
        </div>
        <div className="sm-plist">
          {list.length ? (
            list.map((f) => {
              const last = feedByKey.get(f.key)?.entries[0]?.at;
              return (
                <button
                  key={f.key}
                  type="button"
                  className="sm-pform"
                  onClick={() => set({ form: f.key })}
                >
                  {f.name}
                  <span className="sm-m">
                    {f.fields.length} fields,{' '}
                    {last ? `last filled ${fmtWhen(last)}` : 'not filled yet'}
                  </span>
                </button>
              );
            })
          ) : (
            <p className="sm-blank">
              {forms.length
                ? `No form matches "${picker.q}". Try part of the name.`
                : 'No forms yet. Forms this client fills in show up here.'}
            </p>
          )}
        </div>
      </>
    );
  } else if (picker.tab === 'forms' && form) {
    const avail = form.fields.filter((q) => !has({ t: 'form', f: form.key, q: q.id }));
    const key = (q: string) => `form|${form.key}|${q}`;
    const allOn = avail.length > 0 && avail.every((q) => picker.sel[key(q.id)]);
    body = (
      <>
        <button type="button" className="sm-pback" onClick={() => set({ form: null })}>
          ‹ All forms
        </button>
        <div className="sm-pall">
          <span>{form.name}</span>
          <label>
            <input
              type="checkbox"
              checked={allOn}
              disabled={!avail.length}
              onChange={(e) =>
                set({
                  sel: {
                    ...picker.sel,
                    ...Object.fromEntries(avail.map((q) => [key(q.id), e.target.checked])),
                  },
                })
              }
            />{' '}
            Select all
          </label>
        </div>
        {form.fields.map((q) => {
          const added = has({ t: 'form', f: form.key, q: q.id });
          const a = fieldAnswers(feedByKey.get(form.key), q.id).latest;
          return (
            <label key={q.id} className={`sm-pq${added ? ' sm-added' : ''}`}>
              <input
                type="checkbox"
                checked={added || !!picker.sel[key(q.id)]}
                disabled={added}
                onChange={(e) => toggle(key(q.id), e.target.checked)}
              />
              <span>
                <span>{q.label}</span>
                {added && <span className="sm-tagadded">On this card</span>}
                <br />
                <span className="sm-a">
                  {who}: {a ? a.value : 'not filled yet'}
                </span>
              </span>
            </label>
          );
        })}
      </>
    );
  } else if (picker.tab === 'client') {
    body = SUMMARY_FIELD_KEYS.map((k) => {
      const added = has({ t: 'demo', k });
      return (
        <label key={k} className={`sm-pq${added ? ' sm-added' : ''}`}>
          <input
            type="checkbox"
            checked={added || !!picker.sel[`demo|${k}`]}
            disabled={added}
            onChange={(e) => toggle(`demo|${k}`, e.target.checked)}
          />
          <span>
            {SUMMARY_FIELD_LABELS[k]}
            {added && <span className="sm-tagadded">On this card</span>}
            <br />
            <span className="sm-a">
              {who}: {values[k] || 'not on file'}
            </span>
          </span>
        </label>
      );
    });
  } else {
    const hasNote = has({ t: 'note' });
    body = (
      <>
        <p className="sm-hint">
          A box staff can type in. The text saves to the client&apos;s record, and only people with
          this card on their Summary see it.
        </p>
        <label className={`sm-pq${hasNote ? ' sm-added' : ''}`}>
          <input
            type="checkbox"
            checked={hasNote || !!picker.sel.note}
            disabled={hasNote}
            onChange={(e) => toggle('note', e.target.checked)}
          />
          <span>
            Add a note box to this card
            {hasNote && <span className="sm-tagadded">On this card</span>}
          </span>
        </label>
      </>
    );
  }

  return (
    <div className="sm-modal-overlay" onClick={() => setPicker(null)}>
      <div
        className="sm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Add fields to ${summaryCardTitle(card)}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sm-modal-head">
          <h3>Add fields to &ldquo;{summaryCardTitle(card)}&rdquo;</h3>
          <button
            type="button"
            className="sm-x-close"
            aria-label="Close"
            onClick={() => setPicker(null)}
          >
            {I.x}
          </button>
        </div>
        <div className="sm-ptabs">
          {tabs.map(([t, label]) => (
            <button
              key={t}
              type="button"
              className={`sm-ptab${picker.tab === t ? ' on' : ''}`}
              onClick={() => set({ tab: t })}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="sm-modal-body">{body}</div>
        <div className="sm-modal-foot">
          <span className="sm-count">{n ? `${n} selected` : ''}</span>
          <div className="sm-spacer" />
          <button type="button" className="btn" onClick={() => setPicker(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={!n} onClick={onAdd}>
            {n ? `Add ${n} ${n === 1 ? 'field' : 'fields'}` : 'Add'}
          </button>
        </div>
      </div>
    </div>
  );
}

/** The built-in card titles, for the tab's own tests and previews. */
export const SUMMARY_TITLES = SUMMARY_BUILTIN_TITLES;
